import React, { useState, useEffect } from "react";
import {
  LogIn,
  Eye,
  EyeOff,
  MessageSquare,
  Clock,
  RotateCcw,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { authService } from "../services/api";
import { useNavigate } from "react-router-dom";
import { toEnglishNumbers, toPersianNumbers } from "../utils/numbers";

export default function Login() {
  const [nationalCode, setNationalCode] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Bale OTP states
  const [isOtpMode, setIsOtpMode] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    let timer: any;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown]);

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return toPersianNumbers(
      `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`,
    );
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const res = await authService.login(nationalCode, password);
      if (res.requirePasswordChange) {
        setRequirePasswordChange(true);
      } else {
        navigate("/dashboard");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "خطا در ارتباط با سرور");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    const cleanCode = toEnglishNumbers(nationalCode).trim();
    if (!cleanCode) {
      setError("لطفاً ابتدا کد ملی را وارد کنید.");
      return;
    }
    if (cleanCode.length !== 10) {
      setError("کد ملی باید دقیقاً ۱۰ رقم باشد.");
      return;
    }

    if (countdown > 0) {
      return;
    }

    setOtpLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const res = await authService.sendBaleOtp(cleanCode);
      setIsOtpMode(true);
      setCountdown(res.expiresIn || 120);
      setOtpCode("");
      setSuccessMessage(
        res.message || "کد ۷ رقمی با موفقیت به بله شما ارسال شد.",
      );
    } catch (err: any) {
      const msg = err.response?.data?.message || "خطا در ارتباط با سرور";
      setError(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanCode = toEnglishNumbers(nationalCode).trim();
    const cleanOtp = toEnglishNumbers(otpCode).trim();

    if (!cleanOtp || cleanOtp.length !== 7) {
      setError("لطفاً کد تایید ۷ رقمی را به صورت کامل وارد کنید.");
      return;
    }

    setOtpLoading(true);
    setError("");

    try {
      await authService.loginBaleOtp(cleanCode, cleanOtp);
      navigate("/dashboard");
    } catch (err: any) {
      const msg =
        err.response?.data?.message || "کد وارد شده اشتباه یا منقضی شده است.";
      setError(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await authService.changePasswordFirstLogin({
        nationalCode,
        oldPassword: password,
        newPassword,
      });
      // After success, go back to login flow (they need to login again)
      setRequirePasswordChange(false);
      setPassword("");
      setNewPassword("");
      setError("رمز عبور تغییر یافت. لطفا دوباره وارد شوید.");
    } catch (err: any) {
      setError(err.response?.data?.message || "خطا در تغییر رمز عبور");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-8 bg-[#F8FAFC] dark:bg-slate-900 font-sans text-slate-800 dark:text-slate-100">
      <div className="w-full max-w-[420px] bg-white dark:bg-slate-800 rounded-3xl shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-200 dark:border-slate-700 p-6 sm:p-10 relative overflow-hidden">
        <div className="absolute top-0 right-0 left-0 h-1.5 bg-persian-600 dark:bg-persian-500"></div>
        <div className="mb-6 sm:mb-8 text-center">
          <img
            src="/logo.png"
            alt="لوگوی نهام"
            className="w-32 h-auto mx-auto mb-4"
          />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-2">
            به سامانه نهام خوش آمدید
          </h1>
        </div>

        <div>
          {requirePasswordChange ? (
            <form onSubmit={handleChangePassword} className="space-y-6">
              {error && (
                <div
                  className={`p-3 rounded-lg text-sm text-center ${error.includes("موفقیت") ? "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400" : "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400"}`}
                >
                  {error}
                </div>
              )}

              <div className="bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 p-3 rounded-lg text-sm text-center">
                شما برای اولین بار وارد شده‌اید. لطفا رمز عبور خود را تغییر
                دهید.
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mr-1">
                  رمز عبور جدید
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    dir="ltr"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full h-12 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 text-sm focus:outline-none focus:border-persian-500 focus:ring-4 focus:ring-persian-50/50 dark:focus:ring-persian-500/20 transition-all dark:text-white"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 h-12 bg-persian-600 dark:bg-persian-600 text-white font-bold rounded-xl hover:bg-persian-700 dark:hover:bg-persian-700 transition-colors shadow-lg shadow-persian-900/10 dark:shadow-none disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="animate-pulse">در حال تغییر...</span>
                ) : (
                  <span>تغییر رمز عبور</span>
                )}
              </button>
            </form>
          ) : isOtpMode ? (
            <form onSubmit={handleOtpLogin} className="space-y-6">
              {error && (
                <div className="p-3 rounded-lg text-sm text-center bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}

              {successMessage && !error && (
                <div className="p-3 rounded-lg text-sm text-center bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  <span>{successMessage}</span>
                </div>
              )}

              <div className="bg-slate-50 dark:bg-slate-900/50 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  کد ملی:
                </div>
                <div className="text-sm font-bold text-slate-800 dark:text-slate-200 tracking-wider">
                  {toPersianNumbers(nationalCode)}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mr-1">
                  کد تایید ۷ رقمی بله
                </label>
                <input
                  type="text"
                  dir="ltr"
                  required
                  autoFocus
                  value={toPersianNumbers(otpCode)}
                  onChange={(e) => {
                    const val = toEnglishNumbers(e.target.value)
                      .replace(/\D/g, "")
                      .slice(0, 7);
                    setOtpCode(val);
                  }}
                  className="w-full h-12 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 text-center text-xl font-bold tracking-widest focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50/50 dark:focus:ring-emerald-500/20 transition-all dark:text-white"
                  placeholder="•••••••"
                  maxLength={7}
                />
              </div>

              {/* Countdown / Resend status */}
              <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 px-1">
                <div className="flex items-center gap-1.5 font-medium">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>اعتبار کد:</span>
                  <span
                    className={`font-bold ${countdown <= 20 ? "text-red-500 animate-pulse" : "text-emerald-600 dark:text-emerald-400"}`}
                  >
                    {formatCountdown(countdown)}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleRequestOtp}
                  disabled={countdown > 0 || otpLoading}
                  className={`font-semibold transition-colors flex items-center gap-1 ${
                    countdown > 0 || otpLoading
                      ? "text-slate-400 cursor-not-allowed opacity-60"
                      : "text-persian-600 dark:text-persian-400 hover:text-persian-700 underline cursor-pointer"
                  }`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>ارسال مجدد کد</span>
                </button>
              </div>

              <button
                type="submit"
                disabled={otpLoading || otpCode.length !== 7}
                className="w-full flex items-center justify-center gap-2 h-12 bg-emerald-600 dark:bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 dark:hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-900/10 dark:shadow-none disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {otpLoading ? (
                  <span className="animate-pulse">در حال بررسی...</span>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>ورود به سیستم</span>
                  </>
                )}
              </button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsOtpMode(false);
                    setError("");
                    setSuccessMessage("");
                    setOtpCode("");
                  }}
                  className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors inline-flex items-center gap-1 cursor-pointer"
                >
                  <ArrowRight className="w-3.5 h-3.5" />
                  <span>ورود با رمز عبور</span>
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-6">
              {error && (
                <div
                  className={`p-3 rounded-lg text-sm text-center ${error.includes("موفقیت") || error.includes("وارد شوید") ? "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400" : "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400"}`}
                >
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mr-1">
                  کد ملی
                </label>
                <input
                  type="text"
                  dir="ltr"
                  required
                  value={toPersianNumbers(nationalCode)}
                  onChange={(e) =>
                    setNationalCode(toEnglishNumbers(e.target.value))
                  }
                  className="w-full h-12 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 text-sm focus:outline-none focus:border-persian-500 focus:ring-4 focus:ring-persian-50/50 dark:focus:ring-persian-500/20 transition-all dark:text-white"
                  placeholder="0123456789"
                  maxLength={10}
                  minLength={10}
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mr-1">
                    رمز عبور
                  </label>
                  <a
                    href="#"
                    className="text-xs text-persian-600 dark:text-persian-400 font-medium"
                  >
                    فراموشی رمز؟
                  </a>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    dir="ltr"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-12 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl px-4 pl-10 text-sm focus:outline-none focus:border-persian-500 focus:ring-4 focus:ring-persian-50/50 dark:focus:ring-persian-500/20 transition-all dark:text-white"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || otpLoading}
                className="w-full flex items-center justify-center gap-2 h-12 bg-persian-600 dark:bg-persian-600 text-white font-bold rounded-xl hover:bg-persian-700 dark:hover:bg-persian-700 transition-colors shadow-lg shadow-persian-900/10 dark:shadow-none disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              >
                {loading ? (
                  <span className="animate-pulse">در حال ورود...</span>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>ورود به سیستم</span>
                  </>
                )}
              </button>

              <div className="relative flex py-1 items-center">
                <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
                <span className="flex-shrink mx-4 text-xs text-slate-400 font-medium">
                  یا
                </span>
                <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
              </div>

              <button
                type="button"
                onClick={handleRequestOtp}
                disabled={loading || otpLoading}
                className="w-full flex items-center justify-center gap-2 h-12 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60 font-bold rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {otpLoading ? (
                  <span className="animate-pulse">
                    در حال ارسال کد به بله...
                  </span>
                ) : (
                  <>
                    <MessageSquare className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    <span>ورود با کد یکبار مصرف بله</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
